import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { z } from "zod";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { SocialLinks } from "@/components/SocialLinks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
});

const contactSchema = z.object({
  name: z.string().trim().min(2, "Name is required").max(80),
  phone: z.string().trim().min(10, "Valid phone required").max(20),
  subject: z.string().trim().min(2, "Subject is required").max(120),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(1000),
});

const faqs = [
  { q: "How long does delivery take?", a: "Most orders arrive within 2–5 business days across Pakistan." },
  { q: "Do you offer Cash on Delivery?", a: "Yes — COD is available on all orders nationwide." },
  { q: "Can I return a product?", a: "Yes, returns are accepted within 7 days of delivery if the product is unused and in original packaging." },
  { q: "How can I track my order?", a: "You'll receive tracking details via SMS once your order is dispatched." },
];

function ContactPage() {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const result = contactSchema.safeParse({
      name: fd.get("name"),
      phone: fd.get("phone"),
      subject: fd.get("subject"),
      message: fd.get("message"),
    });
    if (!result.success) {
      toast.error(result.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 600));
    setSubmitting(false);
    toast.success("Message sent! We'll get back to you soon.");
    (e.target as HTMLFormElement).reset();
  };

  return (
    <>
      <Helmet>
        <title>Contact Us</title>
        <meta name="description" content="Get in touch with Trendora Marts. Phone, email and location for trending lifestyle store in Pakistan." />
        <meta property="og:title" content="Contact Trendora Marts" />
      </Helmet>
      <section className="bg-card text-foreground py-16 text-center border-b border-border">
        <div className="container mx-auto px-4">
          <p className="text-gold uppercase text-xs tracking-[0.3em] mb-3">Get in Touch</p>
          <h1 className="text-4xl sm:text-5xl font-serif">We'd Love to Hear From You</h1>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 grid lg:grid-cols-2 gap-12">
        {/* Form */}
        <div className="bg-card border border-border rounded-lg p-8 shadow-soft">
          <h2 className="text-2xl font-serif mb-6">Send a Message</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" name="name" required maxLength={80} />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input id="phone" name="phone" type="tel" required maxLength={20} />
            </div>
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input id="subject" name="subject" required maxLength={120} />
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" name="message" rows={5} required maxLength={1000} />
            </div>
            <Button type="submit" variant="gold" size="lg" className="w-full" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send Message</>}
            </Button>
          </form>
        </div>

        {/* Info */}
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-8">
            <h2 className="text-2xl font-serif mb-6">Contact Information</h2>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-gradient-gold grid place-items-center shrink-0">
                  <Phone className="h-4 w-4 text-gold-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <a href="tel:03707851120" className="font-medium hover:text-gold">0370-7851120</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-gradient-gold grid place-items-center shrink-0">
                  <Mail className="h-4 w-4 text-gold-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <a href="mailto:malikmuzammilfive@gmail.com" className="font-medium hover:text-gold break-all">malikmuzammilfive@gmail.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-gradient-gold grid place-items-center shrink-0">
                  <Mail className="h-4 w-4 text-gold-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Business Email</p>
                  <a href="mailto:trendoramarts052@gmail.com" className="font-medium hover:text-gold break-all">trendoramarts052@gmail.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-gradient-gold grid place-items-center shrink-0">
                  <MapPin className="h-4 w-4 text-gold-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">Lahore, Pakistan</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg overflow-hidden border border-border h-64">
            <iframe
              title="Map"
              src="https://www.google.com/maps?q=Lahore,Pakistan&output=embed"
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <div className="bg-card border border-border rounded-lg p-8 text-center shadow-soft max-w-2xl mx-auto">
          <h2 className="text-2xl font-serif mb-6">Connect With Us</h2>
          <SocialLinks variant="contact" className="justify-center" />
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-10">
            <p className="text-gold uppercase text-xs tracking-[0.3em] mb-3">Help Center</p>
            <h2 className="text-3xl sm:text-4xl font-serif">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group bg-card border border-border rounded-lg p-5">
                <summary className="cursor-pointer font-medium flex justify-between items-center">
                  {f.q}
                  <span className="text-gold transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
